import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Shield, ArrowLeft, Plus, Key, Trash2, Eye, Building, User, Mail, Loader2, Search, AlertTriangle, Download, FileText, Upload, Image } from "lucide-react";
import { toast } from "sonner";
import PasswordInput from "@/components/PasswordInput";
import { getAuthHeaders } from "@/utils/auth";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminPolice() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedPoliceId, setSelectedPoliceId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [tempPassword, setTempPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [badgeUploading, setBadgeUploading] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [authorityName, setAuthorityName] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [badgeNumber, setBadgeNumber] = useState("");

  useEffect(() => { 
    fetchAccounts(); 
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/admin/police/stats`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) { 
      console.error("Failed to fetch stats:", error);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await fetch(`${API}/admin/police`, { headers: getAuthHeaders() });
      if (!response.ok) {
        if (response.status === 403) { toast.error("Admin access required"); navigate("/dashboard"); return; }
        throw new Error("Failed");
      }
      const data = await response.json();
      setAccounts(data.accounts);
    } catch (error) { toast.error(error.message); }
    finally { setLoading(false); }
  };

  const fetchAccountDetail = async (policeId) => {
    try {
      const response = await fetch(`${API}/admin/police/${policeId}`, { headers: getAuthHeaders() });
      if (response.ok) { setSelectedAccount(await response.json()); setShowDetailDialog(true); }
    } catch (error) { toast.error("Failed"); }
  };

  const handleCreate = async () => {
    if (!username || !password || !authorityName || !contactName || !contactEmail) { toast.error("Fill required fields"); return; }
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/police`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        headers: getAuthHeaders(),
        body: JSON.stringify({ username, password, authority_name: authorityName, contact_name: contactName, contact_email: contactEmail, contact_phone: contactPhone, badge_number: badgeNumber })
      });
      if (response.ok) {
        toast.success("Account created!");
        setShowCreateDialog(false);
        setUsername(""); setPassword(""); setAuthorityName(""); setContactName(""); setContactEmail(""); setContactPhone(""); setBadgeNumber("");
        fetchAccounts();
      } else { const data = await response.json(); toast.error(data.detail || "Failed"); }
    } catch (error) { toast.error("Failed"); }
    finally { setActionLoading(false); }
  };

  const handleResetPassword = async (policeId) => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/police/${policeId}/reset-password`, { 
        method: "PUT", 
        headers: getAuthHeaders(),
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ new_password: newPassword })
      });
      if (response.ok) { 
        toast.success("Password updated successfully!"); 
        setShowPasswordDialog(false);
        setNewPassword("");
        setSelectedPoliceId(null);
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to update password");
      }
    } catch (error) { toast.error("Failed"); }
    finally { setActionLoading(false); }
  };

  const openPasswordDialog = (policeId) => {
    setSelectedPoliceId(policeId);
    setNewPassword("");
    setShowPasswordDialog(true);
  };

  const handleToggleActive = async (policeId, currentStatus) => {
    try {
      const response = await fetch(`${API}/admin/police/${policeId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_active: !currentStatus })
      });
      if (response.ok) { toast.success(currentStatus ? "Deactivated" : "Activated"); fetchAccounts(); }
    } catch (error) { toast.error("Failed"); }
  };

  const handleDelete = async (policeId) => {
    if (!confirm("Delete this police account?")) return;
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/police/${policeId}`, { method: "DELETE", headers: getAuthHeaders() });
      if (response.ok) { toast.success("Deleted"); setShowDetailDialog(false); fetchAccounts(); }
    } catch (error) { toast.error("Failed"); }
    finally { setActionLoading(false); }
  };

  const handleBadgeUpload = async (policeId, file) => {
    setBadgeUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await fetch(`${API}/admin/police/${policeId}/badge-image`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: formData
      });
      if (response.ok) {
        const data = await response.json();
        toast.success("Badge image uploaded!");
        setSelectedAccount({ ...selectedAccount, badge_image_url: data.badge_image_url });
        fetchAccounts();
      } else {
        toast.error("Upload failed");
      }
    } catch (error) {
      toast.error("Upload failed");
    } finally {
      setBadgeUploading(false);
    }
  };

  // Police Report Export Button Component
  const PoliceReportExportButton = () => {
    const [exporting, setExporting] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [showSelector, setShowSelector] = useState(false);

    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];

    const years = [];
    const currentYear = new Date().getFullYear();
    for (let y = currentYear; y >= currentYear - 2; y--) {
      years.push(y);
    }

    const handleExport = async () => {
      setExporting(true);
      try {
        const response = await fetch(
          `${API}/admin/police/report/export?month=${selectedMonth}&year=${selectedYear}`, 
          { headers: getAuthHeaders() }
        );
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `police_activity_report_${months[selectedMonth - 1]}_${selectedYear}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          toast.success(`Police activity report exported!`);
          setShowSelector(false);
        } else {
          throw new Error("Export failed");
        }
      } catch (error) {
        toast.error("Export failed");
      } finally {
        setExporting(false);
      }
    };

    return (
      <div className="relative">
        <Button 
          size="sm" 
          variant="outline" 
          onClick={() => setShowSelector(!showSelector)}
          className="border-blue-600 text-blue-400 hover:bg-blue-900/30"
        >
          <Download className="w-4 h-4 mr-2" />
          Export Monthly Report
        </Button>
        
        {showSelector && (
          <div className="absolute right-0 top-full mt-2 bg-slate-800 border border-blue-700 rounded-lg p-4 z-50 shadow-xl min-w-[220px]">
            <p className="text-xs text-blue-400 mb-2 font-semibold">Select Report Period:</p>
            <div className="flex gap-2 mb-3">
              <select 
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="flex-1 bg-slate-900 border border-blue-800 rounded px-2 py-1 text-sm text-white"
              >
                {months.map((m, idx) => (
                  <option key={m} value={idx + 1}>{m}</option>
                ))}
              </select>
              <select 
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="bg-slate-900 border border-blue-800 rounded px-2 py-1 text-sm text-white"
              >
                {years.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <p className="text-xs text-blue-300/70 mb-3">
              Includes: Lookups by force, stolen tools found, values, and detailed log
            </p>
            <div className="flex gap-2">
              <Button 
                size="sm" 
                onClick={handleExport}
                disabled={exporting}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs"
              >
                {exporting ? "Exporting..." : "Download CSV"}
              </Button>
              <Button 
                size="sm" 
                variant="ghost"
                onClick={() => setShowSelector(false)}
                className="text-gray-400 text-xs"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-3 lg:px-8">
          <div className="flex items-center gap-4">
            <Link to="/admin"><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-2" />Admin</Button></Link>
            <div className="flex items-center gap-2"><Shield className="w-6 h-6 text-blue-400" /><span className="font-heading text-lg font-bold uppercase">Police Accounts</span></div>
          </div>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setShowCreateDialog(true)} data-testid="create-police-btn"><Plus className="w-4 h-4 mr-2" />Add Authority</Button>
        </div>
      </header>

      <main className="p-4 lg:p-8 max-w-6xl mx-auto">
        {/* Stats Boxes */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-blue-700/50 bg-blue-900/20">
            <CardContent className="p-4">
              <p className="text-blue-400 text-xs uppercase">Total Lookups</p>
              <p className="text-2xl font-heading font-bold text-white">{stats?.total_lookups ?? "-"}</p>
            </CardContent>
          </Card>
          <Card className="border-red-700/50 bg-red-900/20">
            <CardContent className="p-4">
              <p className="text-red-400 text-xs uppercase flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Stolen Hits
              </p>
              <p className="text-2xl font-heading font-bold text-red-400">{stats?.stolen_hits ?? "-"}</p>
            </CardContent>
          </Card>
          <Card className="border-green-700/50 bg-green-900/20">
            <CardContent className="p-4">
              <p className="text-green-400 text-xs uppercase">Active Accounts</p>
              <p className="text-2xl font-heading font-bold text-green-400">{stats?.active_accounts ?? "-"}</p>
            </CardContent>
          </Card>
          <Card className="border-border bg-card">
            <CardContent className="p-4">
              <p className="text-muted-foreground text-xs uppercase">Total Accounts</p>
              <p className="text-2xl font-heading font-bold">{stats?.total_accounts ?? "-"}</p>
            </CardContent>
          </Card>
        </div>

        {/* Monthly Report Export */}
        <Card className="border-blue-700/50 bg-blue-900/10 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <FileText className="w-6 h-6 text-blue-400" />
                <div>
                  <h3 className="font-heading text-sm font-bold text-white uppercase">Monthly Activity Report</h3>
                  <p className="text-blue-300 text-xs">Export lookups by police force, stolen tools found, and detailed activity log</p>
                </div>
              </div>
              <PoliceReportExportButton />
            </div>
          </CardContent>
        </Card>

        <div className="mb-6 p-4 bg-blue-900/20 border border-blue-700/50 rounded">
          <p className="text-blue-300 text-sm"><Shield className="w-4 h-4 inline mr-2" />Police accounts access stolen tool database at <strong>/police</strong></p>
        </div>

        {loading ? (
          <div className="text-center py-12"><Loader2 className="w-8 h-8 text-blue-400 animate-spin mx-auto mb-4" /></div>
        ) : accounts.length === 0 ? (
          <Card className="border-border bg-card"><CardContent className="py-12 text-center"><Shield className="w-12 h-12 text-blue-400 mx-auto mb-4 opacity-50" /><p className="text-muted-foreground mb-4">No accounts yet</p></CardContent></Card>
        ) : (
          <div className="space-y-4">
            {accounts.map((acc) => (
              <Card key={acc.police_id} className={`border-border bg-card ${!acc.is_active ? "opacity-60" : ""}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    {/* Authority Info */}
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="p-3 bg-blue-600/20 rounded-lg flex-shrink-0">
                        {acc.badge_image_url ? (
                          <img src={acc.badge_image_url} alt="" className="w-6 h-6 object-contain" />
                        ) : (
                          <Building className="w-6 h-6 text-blue-400" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-heading text-lg uppercase truncate">{acc.authority_name}</h3>
                          {!acc.is_active && <Badge variant="destructive">Inactive</Badge>}
                        </div>
                        <p className="text-sm text-muted-foreground truncate"><User className="w-3 h-3 inline mr-1" />{acc.contact_name}</p>
                        <p className="text-xs text-muted-foreground truncate"><Mail className="w-3 h-3 inline mr-1" />{acc.contact_email} <Search className="w-3 h-3 inline ml-2 mr-1" />{acc.lookup_count || 0} lookups</p>
                      </div>
                    </div>
                    
                    {/* Action Controls - Inline on mobile */}
                    <div className="flex items-center gap-3 border-t sm:border-t-0 pt-2 sm:pt-0 mt-1 sm:mt-0">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <div className="scale-[0.65] sm:scale-100 origin-left -mr-1 sm:mr-0">
                          <Switch checked={acc.is_active} onCheckedChange={() => handleToggleActive(acc.police_id, acc.is_active)} />
                        </div>
                        <span className="text-xs text-muted-foreground">Active</span>
                      </label>
                      <div className="flex items-center ml-auto sm:ml-0">
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => fetchAccountDetail(acc.police_id)} title="View Details"><Eye className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openPasswordDialog(acc.police_id)} title="Reset Password"><Key className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive" onClick={() => handleDelete(acc.police_id)} title="Delete"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="bg-card border-border max-w-lg">
            <DialogHeader><DialogTitle className="font-heading uppercase">Create Police Account</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Username *</Label><Input value={username} onChange={(e) => setUsername(e.target.value)} className="bg-background" data-testid="new-username" /></div>
                <div><Label>Password *</Label><PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} className="bg-background pr-10" data-testid="new-password" /></div>
              </div>
              <div><Label>Authority Name *</Label><Input value={authorityName} onChange={(e) => setAuthorityName(e.target.value)} placeholder="Metro Police Dept" className="bg-background" data-testid="new-authority" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Contact Name *</Label><Input value={contactName} onChange={(e) => setContactName(e.target.value)} className="bg-background" data-testid="new-contact" /></div>
                <div><Label>Badge #</Label><Input value={badgeNumber} onChange={(e) => setBadgeNumber(e.target.value)} className="bg-background" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Email *</Label><Input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className="bg-background" data-testid="new-email" /></div>
                <div><Label>Phone</Label><Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} className="bg-background" /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={actionLoading} className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-create-btn">{actionLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="bg-card border-border max-w-2xl">
            <DialogHeader><DialogTitle className="font-heading uppercase">{selectedAccount?.authority_name}</DialogTitle></DialogHeader>
            {selectedAccount && (
              <div className="space-y-4">
                {/* Force Badge Image */}
                <div className="flex items-center gap-4 p-4 bg-secondary/10 rounded-lg border border-border">
                  <div className="w-24 h-24 rounded-lg border-2 border-dashed border-border flex items-center justify-center overflow-hidden bg-background flex-shrink-0">
                    {selectedAccount.badge_image_url ? (
                      <img src={selectedAccount.badge_image_url} alt="Force Badge" className="w-full h-full object-contain p-1" />
                    ) : (
                      <Image className="w-8 h-8 text-muted-foreground opacity-30" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm mb-1">Force Badge / Crest</p>
                    <p className="text-xs text-muted-foreground mb-2">Displayed on their police portal header</p>
                    <label className="cursor-pointer inline-block">
                      <span className="inline-flex items-center justify-center h-9 px-4 rounded-md border border-input bg-background text-sm font-medium hover:bg-accent hover:text-accent-foreground">
                        {badgeUploading ? <Loader2 className="w-3 h-3 animate-spin mr-2" /> : <Upload className="w-3 h-3 mr-2" />}
                        {selectedAccount.badge_image_url ? "Change" : "Upload"}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={badgeUploading}
                        onChange={(e) => {
                          if (e.target.files?.[0]) handleBadgeUpload(selectedAccount.police_id, e.target.files[0]);
                        }}
                        data-testid="badge-upload-input"
                      />
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-secondary/20 rounded"><p className="text-xs text-muted-foreground">Username</p><p className="font-mono">{selectedAccount.username}</p></div>
                  <div className="p-3 bg-secondary/20 rounded"><p className="text-xs text-muted-foreground">Contact</p><p>{selectedAccount.contact_name}</p></div>
                  <div className="p-3 bg-secondary/20 rounded"><p className="text-xs text-muted-foreground">Email</p><p>{selectedAccount.contact_email}</p></div>
                  <div className="p-3 bg-secondary/20 rounded"><p className="text-xs text-muted-foreground">Lookups</p><p className="text-xl font-heading">{selectedAccount.lookup_count || 0}</p></div>
                </div>
                {tempPassword && <div className="p-4 bg-blue-600/20 border border-blue-600/50 rounded"><p className="text-sm font-medium mb-2">New Password</p><p className="font-mono text-lg text-blue-400">{tempPassword}</p></div>}
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
          <DialogContent className="bg-card border-border max-w-md">
            <DialogHeader><DialogTitle className="font-heading uppercase">Set New Password</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>New Password *</Label>
                <PasswordInput 
                  value={newPassword} 
                  onChange={(e) => setNewPassword(e.target.value)} 
                  placeholder="Enter new password (min 8 characters)"
                  className="bg-background pr-10" 
                  data-testid="new-police-password" 
                />
              </div>
              <p className="text-sm text-muted-foreground">
                This will immediately change the password for this police account.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>Cancel</Button>
              <Button 
                onClick={() => handleResetPassword(selectedPoliceId)} 
                disabled={actionLoading || !newPassword} 
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="confirm-password-btn"
              >
                {actionLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Update Password
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
